<template>
  <div class="field" :style="field.style" v-if="local_value && condition">
    <label class="label">
      {{ local_value.label }}
      {{ local_value.required ? " *" : "" }}
    </label>

    <p class="help mb-2">
      Kërko në të gjithë katalogun (jo vetëm aktivët / online). Shkruaj kod ose emër (min. 2 shkronja), pastaj kliko rezultatin.
    </p>

    <div class="is-flex mb-2" style="gap: 0.5rem">
      <input
        class="input is-small"
        type="text"
        v-model="searchQuery"
        placeholder="Kodi, emri, modeli..."
        @input="onSearchInput"
        @keyup.enter="runSearch"
      />
      <span v-if="searching" class="is-size-7 has-text-grey" style="align-self: center">
        <i class="fa fa-spinner fa-spin"></i>
      </span>
    </div>

    <div v-if="searchResults.length > 0" class="box is-paddingless mb-3" style="max-height: 200px; overflow-y: auto">
      <a
        v-for="result in searchResults"
        :key="result.guid"
        href=""
        class="dropdown-item is-size-7 py-2 px-3"
        style="display: block; border-bottom: 1px solid #eee"
        @click.prevent="addFromSearch(result)"
      >
        {{ result.label }}
      </a>
    </div>

    <div v-if="searchError" class="help is-danger mb-2">{{ searchError }}</div>
    <div v-else-if="searchQuery.length >= 2 && !searching && searchResults.length === 0 && searchTouched" class="help mb-2">
      Nuk u gjet asnjë produkt (kërkohet produkt jo-kompozit, jo i delistuar).
    </div>

    <div v-for="(row, index) in local_value.data" :key="row._key || index" class="box py-2 px-3 mb-2">
      <div class="is-flex is-align-items-center is-justify-content-space-between">
        <div class="is-size-7">
          <strong>{{ row.label || row.component_code || "Produkt" }}</strong>
        </div>
        <a href="" class="button is-small is-danger is-light" @click.prevent="removeRow(index)" title="Hiq">
          <i class="fa fa-trash"></i>
        </a>
      </div>
      <div class="is-flex is-align-items-center mt-2" style="gap: 0.5rem">
        <label class="is-size-7 mb-0">Sasia:</label>
        <input
          class="input is-small"
          type="number"
          min="0.0001"
          step="any"
          style="width: 100px"
          v-model.number="row.quantity"
          @input="$emit('syncField', local_value)"
        />
      </div>
    </div>

    <p class="help is-danger" v-if="local_value.required && (!local_value.data || local_value.data.length === 0)">
      Shtoni të paktën një komponent
    </p>
  </div>
</template>

<script>
import ProductsServices, { isValidProductGuid } from "@/services/Product";

export default {
  props: ["field", "fields"],
  data() {
    return {
      local_value: null,
      searchQuery: "",
      searchResults: [],
      searching: false,
      searchTouched: false,
      searchTimer: null,
      searchError: null,
    };
  },
  computed: {
    condition() {
      let is_visible = true;
      const vifRules = this.local_value?.vif ?? this.field?.vif;
      if (vifRules && this.fields) {
        this.fields.forEach((f) => {
          const rows = Array.isArray(f) ? f : [f];
          rows.forEach((ff) => {
            vifRules.forEach((c) => {
              if (ff.name === c.field) {
                if (c.value_not !== undefined) {
                  is_visible = ff.value != c.value_not;
                }
                if (c.value !== undefined) {
                  is_visible = ff.value == c.value;
                }
              }
            });
          });
        });
      }
      return is_visible;
    },
    excludeGuid() {
      const fromField = this.local_value?.exclude_product_guid;
      if (isValidProductGuid(fromField)) {
        return fromField.trim();
      }
      return null;
    },
    selectedGuids() {
      return (this.local_value?.data || []).map((r) => r.component_product_guid).filter(Boolean);
    },
  },
  methods: {
    onSearchInput() {
      if (this.searchTimer) {
        clearTimeout(this.searchTimer);
      }
      this.searchTimer = setTimeout(() => {
        this.runSearch();
      }, 300);
    },
    async runSearch() {
      const q = (this.searchQuery || "").trim();
      if (q.length < 2) {
        this.searchResults = [];
        this.searchTouched = false;
        return;
      }
      this.searching = true;
      this.searchTouched = true;
      this.searchError = null;
      try {
        const results = await ProductsServices.searchComponents(q, this.excludeGuid);
        const list = Array.isArray(results) ? results : results?.data || [];
        this.searchResults = list.filter((r) => !this.selectedGuids.includes(r.guid));
      } catch (e) {
        this.searchResults = [];
        const err = e?.response?.data;
        const firstFieldError =
          err?.errors?.q?.[0] || err?.errors?.exclude_guid?.[0] || Object.values(err?.errors || {})[0]?.[0];
        this.searchError = firstFieldError || err?.message || "Gabim gjatë kërkimit.";
      } finally {
        this.searching = false;
      }
    },
    addFromSearch(result) {
      if (!this.local_value.data) {
        this.local_value.data = [];
      }
      if (this.selectedGuids.includes(result.guid)) {
        return;
      }
      const componentRef = (result.guid || result.component_product_guid || result.code || "").trim();
      if (!componentRef) {
        this.searchError = "Produkti nuk ka identifikues të vlefshëm (guid/kod).";
        return;
      }
      this.local_value.data.push({
        _key: Date.now() + Math.random(),
        component_product_guid: componentRef,
        component_code: result.code || null,
        quantity: 1,
        label: result.label,
      });
      this.searchQuery = "";
      this.searchResults = [];
      this.searchTouched = false;
      this.$emit("syncField", this.local_value);
    },
    removeRow(index) {
      this.local_value.data.splice(index, 1);
      this.$emit("syncField", this.local_value);
    },
  },
  created() {
    this.local_value = { ...this.field };
    if (!Array.isArray(this.local_value.data)) {
      this.local_value.data = [];
    }
    if (!isValidProductGuid(this.local_value.exclude_product_guid)) {
      delete this.local_value.exclude_product_guid;
    }
  },
  beforeUnmount() {
    if (this.searchTimer) {
      clearTimeout(this.searchTimer);
    }
  },
};
</script>
